// Cash denomination breakdown for OPD payments (InvoicePayment + appointment PaymentModal).
// Shown only when the facility has `cash_denomination_enabled` (from get_invoice_config/)
// and the payment type is CASH; the backend then requires the breakdown too.
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

// Accepted Indian denominations — the ₹1,000 note is withdrawn and intentionally absent.
export const CASH_NOTES = [2000, 500, 200, 100, 50, 20, 10, 5, 1];
export const CASH_COINS = [20, 10, 5, 2, 1];

const keyOf = (type, denomination) => `${type}-${denomination}`;

// Quantities are signed whole numbers: positive = tendered, negative = change handed back
// (₹1400 paid with a ₹2000 note is ₹2000 × 1, ₹200 × -3). The partial form is what the
// input accepts while typing ("", "-", "+").
const QTY_TYPING = /^[+-]?\d*$/;
const QTY = /^[+-]?\d+$/;

const toQty = (raw) => (QTY.test(String(raw ?? "")) ? parseInt(raw, 10) : 0);

const formatRupees = (n) => `${n < 0 ? "-" : ""}₹${Math.abs(n).toFixed(2)}`;

/**
 * values = { "NOTE-500": "2", "COIN-5": "3", "NOTE-200": "-3", ... }
 * `due` is the payment amount rounded to the nearest rupee (there are no paise coins), and
 * `roundOff` is what that rounding adds to the payment amount.
 */
export function cashDenominationBreakdown(values, paymentAmount) {
  let tendered = 0;
  let change = 0;
  [["NOTE", CASH_NOTES], ["COIN", CASH_COINS]].forEach(([type, list]) => {
    list.forEach((d) => {
      const amount = d * toQty(values?.[keyOf(type, d)]);
      if (amount > 0) tendered += amount;
      else change -= amount;
    });
  });
  const amount = Number(paymentAmount);
  const due = Math.round(amount);
  return { tendered, change, net: tendered - change, due, roundOff: due - amount };
}

/**
 * Message explaining why the payment is blocked, or null when it is fine. Denominations are
 * mandatory and the net (tendered minus change) must equal the amount to collect.
 */
export function cashDenominationMismatch(values, paymentAmount) {
  const { tendered, change, net, due } = cashDenominationBreakdown(values, paymentAmount);
  if (tendered <= 0) {
    return change > 0
      ? "Enter the notes/coins received. Negative quantities are only for change returned."
      : "Cash denomination is required. Please enter the notes/coins to continue.";
  }
  if (!Number.isFinite(due)) return null;
  if (net === due) return null;
  return `Net cash (${formatRupees(net)}) does not match the amount to collect (${formatRupees(due)}). Please correct the denominations to continue.`;
}

/** Only the denominations the user actually entered, in the API payload shape. */
export function buildCashDenominationsPayload(values) {
  const rows = [];
  [["NOTE", CASH_NOTES], ["COIN", CASH_COINS]].forEach(([type, list]) => {
    list.forEach((d) => {
      const quantity = toQty(values?.[keyOf(type, d)]);
      if (quantity !== 0) rows.push({ type, denomination: d, quantity });
    });
  });
  return rows;
}

/**
 * Reads `cash_denomination_enabled` from get_invoice_config/ each time the dialog opens.
 * Anything other than an explicit true (including a failed call) keeps the feature hidden,
 * so facilities without the flag see the screen exactly as before.
 */
export function useCashDenominationEnabled(open, baseUrl, headers) {
  const [enabled, setEnabled] = useState(false);
  const headersRef = useRef(headers);
  headersRef.current = headers;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    axios
      .get(`${baseUrl}get_invoice_config/`, { headers: headersRef.current })
      .then((res) => {
        if (!cancelled) setEnabled(res?.data?.cash_denomination_enabled === true);
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, baseUrl]);

  return enabled;
}

const inputStyle = {
  width: 70,
  padding: "4px 6px",
  borderRadius: 6,
  border: "1px solid #ced4da",
  textAlign: "right",
};

function DenominationGroup({ title, type, list, values, onChange }) {
  return (
    <div style={{ flex: 1, minWidth: 240 }}>
      <div style={{ fontWeight: 600, marginBottom: 6, color: "#1c2c4c" }}>{title}</div>
      {list.map((d) => {
        const qty = toQty(values?.[keyOf(type, d)]);
        return (
          <div
            key={d}
            style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}
          >
            <span style={{ width: 52 }}>₹{d}</span>
            <span>×</span>
            <input
              type="text"
              placeholder="0"
              value={values?.[keyOf(type, d)] ?? ""}
              onChange={(e) => {
                if (QTY_TYPING.test(e.target.value)) onChange(keyOf(type, d), e.target.value);
              }}
              style={inputStyle}
            />
            <span style={{ marginLeft: "auto", color: "#495057" }}>
              {formatRupees(d * qty)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function CashDenominationInput({ values, onChange, paymentAmount, title = "Cash Denomination" }) {
  const { tendered, change, net, due, roundOff } = cashDenominationBreakdown(values, paymentAmount);
  const expected = Number(paymentAmount);
  const hasExpected = Number.isFinite(expected) && expected > 0;
  const mismatch = tendered > 0 && hasExpected && net !== due;
  const hasRoundOff = hasExpected && Math.abs(roundOff) > 0.001;

  const handleChange = (key, value) => onChange({ ...(values || {}), [key]: value });

  return (
    <div
      style={{
        marginTop: 16,
        padding: 12,
        border: "1px solid #bfdbfe",
        borderRadius: 8,
        background: "#f8fbff",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4, color: "#1c2c4c" }}>
        {title}
      </div>
      <div style={{ fontSize: 12, color: "#6c757d", marginBottom: 10 }}>
        Enter a minus quantity for change given back, e.g. ₹2000 × 1 and ₹200 × -3 for ₹1400.
      </div>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        <DenominationGroup
          title="Notes"
          type="NOTE"
          list={CASH_NOTES}
          values={values}
          onChange={handleChange}
        />
        <DenominationGroup
          title="Coins"
          type="COIN"
          list={CASH_COINS}
          values={values}
          onChange={handleChange}
        />
      </div>
      {change > 0 && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: "1px solid #dbeafe",
            display: "flex",
            justifyContent: "space-between",
            color: "#495057",
          }}
        >
          <span>Tendered {formatRupees(tendered)}</span>
          <span>Change {formatRupees(change)}</span>
        </div>
      )}
      <div
        style={{
          marginTop: 8,
          paddingTop: change > 0 ? 0 : 8,
          borderTop: change > 0 ? "none" : "1px solid #dbeafe",
          display: "flex",
          justifyContent: "space-between",
          fontWeight: 600,
          color: mismatch ? "#d32f2f" : "#1c2c4c",
        }}
      >
        <span>Net cash</span>
        <span>
          {formatRupees(net)}
          {mismatch ? ` (to collect ${formatRupees(due)})` : ""}
        </span>
      </div>
      {hasRoundOff && (
        <div style={{ marginTop: 4, fontSize: 12, color: "#495057" }}>
          Cash to collect is {formatRupees(due)} for {formatRupees(expected)} (round-off {formatRupees(roundOff)}).
        </div>
      )}
      {tendered <= 0 && (
        <div style={{ marginTop: 4, fontSize: 12, color: "#d32f2f" }}>
          Cash denomination is required before you can mark this as paid.
        </div>
      )}
      {mismatch && (
        <div style={{ marginTop: 4, fontSize: 12, color: "#d32f2f" }}>
          Net cash must match the amount to collect before you can mark this as paid.
        </div>
      )}
    </div>
  );
}
